/**
 * Editing a part, rather than reading one.
 *
 * The Parts panel could add a part and toggle one off, and that was all. To
 * change a number or swap a colour you had to find the part in the JSON and
 * edit it there, which asks somebody to learn the format before they can nudge
 * a rectangle two pixels left. The format is the point of the product, but it
 * should be something you end up understanding, not something you have to
 * understand first.
 *
 * Every control here is generated from the shape's own field table, the same
 * table the validator checks against. Nothing is hand-written per shape, so a
 * new shape is editable the day it is added, and a field that changes type
 * cannot leave a control behind that writes the wrong thing.
 *
 * Optional fields are shown with their default in place and marked as unset.
 * Hiding them would leave half of what a shape can do invisible; showing them
 * as though they were set would fill the JSON with values nobody chose.
 */

import { GLYPHS, type Kit } from "../src/kit.js";
import type { FieldDef, Part, ShapeFields } from "../src/spec.js";
import { css } from "./colour.js";

export interface EditorOptions {
	part: Part;
	fields: ShapeFields;
	kit: Kit;
	/** Called with the whole replacement part on every change. */
	onChange: (part: Part) => void;
}

/** A labelled row. Every control in here is one of these. */
function row(name: string, def: FieldDef, control: Node, unset: boolean, clear?: () => void) {
	const wrap = document.createElement("div");
	wrap.className = "field";
	wrap.dataset.unset = unset ? "1" : "0";

	const label = document.createElement("label");
	label.className = "field-name";
	label.textContent = name;

	/*
	 * Whether this value is the shape's own or one somebody chose.
	 *
	 * This was the word "set" or "default" next to a bare ×, which said neither
	 * what the state meant nor what the × would do. Both now say it in words: an
	 * untouched field reads "using default", and a changed one offers "Reset",
	 * which is the only thing that button has ever done.
	 */
	if (!def.required) {
		if (unset) {
			const note = document.createElement("span");
			note.className = "field-note";
			note.textContent = "using default";
			label.append(note);
		} else if (clear) {
			const undo = document.createElement("button");
			undo.type = "button";
			undo.className = "field-reset";
			undo.title = `Put ${name} back to ${JSON.stringify(def.default)}, the default for this shape`;
			undo.textContent = "Reset";
			undo.addEventListener("click", clear);
			label.append(undo);
		}
	}

	wrap.append(label, control);
	return wrap;
}

/** A number box. Used on its own and three or four at a time. */
function number(value: number, step: number, onInput: (n: number) => void, label?: string) {
	const input = document.createElement("input");
	input.type = "number";
	input.className = "field-num";
	input.step = String(step);
	input.value = String(value);
	if (label) input.setAttribute("aria-label", label);
	input.addEventListener("input", () => {
		const n = Number(input.value);
		if (Number.isFinite(n)) onInput(n);
	});
	return input;
}

/** A picker over a fixed list of names. Roles, ramps, glyphs, enums, edges. */
function choose(options: string[], value: string, onPick: (v: string) => void, kit?: Kit) {
	const select = document.createElement("select");
	select.className = "field-pick";
	for (const name of options) {
		const opt = document.createElement("option");
		opt.value = name;
		opt.textContent = name;
		opt.selected = name === value;
		select.append(opt);
	}
	// A value the kit no longer has still has to be shown, or changing kits would
	// silently rewrite a part to whatever happened to be first in the list.
	if (value && !options.includes(value)) {
		const opt = document.createElement("option");
		opt.value = value;
		opt.textContent = `${value} (missing)`;
		opt.selected = true;
		select.prepend(opt);
	}
	select.addEventListener("change", () => onPick(select.value));

	if (!kit) return select;

	// A role name means nothing without the colour next to it.
	const wrap = document.createElement("div");
	wrap.className = "field-role";
	const dot = document.createElement("span");
	dot.className = "field-dot checker";
	const fill = document.createElement("i");
	const paint = () => {
		const rgba = kit.roles[select.value];
		fill.style.background = rgba ? css(rgba) : "transparent";
	};
	paint();
	select.addEventListener("change", paint);
	dot.append(fill);
	wrap.append(dot, select);
	return wrap;
}

/** Several numbers side by side, for a box or a point. */
function numbers(values: number[], names: string[], onSet: (v: number[]) => void) {
	const wrap = document.createElement("div");
	wrap.className = "field-nums";
	const held = [...values];
	values.forEach((v, i) => {
		wrap.append(
			number(
				v,
				1,
				(n) => {
					held[i] = n;
					onSet([...held]);
				},
				names[i],
			),
		);
	});
	return wrap;
}

/**
 * The control for one field, chosen by its declared type.
 *
 * The one place in the app that maps the eleven field types onto interface. A
 * type with no case here falls through to a text box that writes JSON, which is
 * worse than a proper control but better than a field you cannot reach.
 */
function control(name: string, def: FieldDef, value: unknown, kit: Kit, set: (v: unknown) => void): Node {
	switch (def.type) {
		case "role":
			return choose(Object.keys(kit.roles), String(value ?? "none"), set, kit);

		case "ramp":
			return choose(Object.keys(kit.ramps), String(value ?? ""), set);

		case "glyph":
			return choose(Object.keys(GLYPHS).sort(), String(value ?? ""), set);

		case "edge":
			return choose(["top", "bottom", "left", "right"], String(value ?? "top"), set);

		case "enum":
			return choose(def.values ?? [], String(value ?? def.values?.[0] ?? ""), set);

		case "bool": {
			const input = document.createElement("input");
			input.type = "checkbox";
			input.className = "field-check";
			input.checked = value === true;
			input.addEventListener("change", () => set(input.checked));
			return input;
		}

		case "box":
			return numbers(Array.isArray(value) ? (value as number[]) : [0, 0, 0, 0], ["top", "right", "bottom", "left"], set);

		case "point":
			return numbers(Array.isArray(value) ? (value as number[]) : [0, 0], ["x", "y"], set);

		case "int":
			return number(Number(value ?? 0), 1, (n) => set(Math.round(n)));

		case "number":
			return number(Number(value ?? 0), 1, set);

		case "value": {
			/*
			 * A value is either a number from 0 to 1 or the name of a drive, which
			 * is what makes a part animate. A slider alone would remove the second
			 * half; a text box alone would make the common case fiddly. Both, with
			 * the slider disabled while a drive is named.
			 */
			const wrap = document.createElement("div");
			wrap.className = "field-value";
			const named = typeof value === "string";
			const slider = document.createElement("input");
			slider.type = "range";
			slider.min = "0";
			slider.max = "1";
			slider.step = "0.05";
			slider.value = String(named ? 1 : Number(value ?? 1));
			slider.disabled = named;
			slider.setAttribute("aria-label", `${name} amount`);

			const text = document.createElement("input");
			text.type = "text";
			text.className = "field-text";
			text.value = String(value ?? 1);
			text.setAttribute("aria-label", `${name}, a number or the name of a drive`);

			slider.addEventListener("input", () => {
				text.value = slider.value;
				set(Number(slider.value));
			});
			text.addEventListener("input", () => {
				const n = Number(text.value);
				const isNumber = text.value.trim() !== "" && Number.isFinite(n);
				slider.disabled = !isNumber;
				if (isNumber) slider.value = String(n);
				set(isNumber ? n : text.value.trim());
			});
			wrap.append(slider, text);
			return wrap;
		}

		case "roles": {
			/* A ramp written inline: an ordered list of roles, dark to light. */
			const wrap = document.createElement("div");
			wrap.className = "field-list";
			const held = Array.isArray(value) ? [...(value as string[])] : [];
			const draw = () => {
				wrap.replaceChildren();
				held.forEach((r, i) => {
					const line = document.createElement("div");
					line.className = "field-line";
					line.append(
						choose(
							Object.keys(kit.roles),
							r,
							(v) => {
								held[i] = v;
								set([...held]);
							},
							kit,
						),
					);
					const drop = document.createElement("button");
					drop.type = "button";
					drop.className = "btn tiny";
					drop.textContent = "Remove";
					drop.addEventListener("click", () => {
						held.splice(i, 1);
						set([...held]);
						draw();
					});
					line.append(drop);
					wrap.append(line);
				});
				const add = document.createElement("button");
				add.type = "button";
				add.className = "btn tiny";
				add.textContent = "Add a colour";
				add.addEventListener("click", () => {
					held.push(Object.keys(kit.roles)[0] ?? "none");
					set([...held]);
					draw();
				});
				wrap.append(add);
			};
			draw();
			return wrap;
		}

		default: {
			const input = document.createElement("input");
			input.type = "text";
			input.className = "field-text";
			input.value = JSON.stringify(value ?? null);
			input.addEventListener("input", () => {
				try {
					set(JSON.parse(input.value));
					input.removeAttribute("aria-invalid");
				} catch {
					input.setAttribute("aria-invalid", "true");
				}
			});
			return input;
		}
	}
}

/**
 * Every field of one part, as controls.
 *
 * Each change hands back a whole replacement part rather than mutating in
 * place, because the sprite is redrawn from the JSON and a mutated object would
 * make the two disagree about what is on screen.
 */
export function buildFields({ part, fields, kit, onChange }: EditorOptions): HTMLElement {
	const form = document.createElement("div");
	form.className = "fields";

	for (const [name, def] of Object.entries(fields)) {
		const unset = !(name in part);
		const shown = unset ? def.default : part[name];

		const set = (v: unknown) => onChange({ ...part, [name]: v } as Part);
		const clear = () => {
			const next = { ...part } as Record<string, unknown>;
			delete next[name];
			onChange(next as Part);
		};

		form.append(row(name, def, control(name, def, shown, kit, set), unset, clear));
	}

	return form;
}
